import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Search, AlertTriangle, TrendingUp, TrendingDown, Check, X, 
  Download, Edit2, Save, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown,
  Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingOverlay from '../components/LoadingOverlay';
import ErrorState from '../components/ErrorState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BufferReview = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState({ too_high: [], too_low: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/buffer-review`, { headers: getAuthHeaders() });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching buffer review data:', error);
      setError('Unable to load buffer review data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [getAuthHeaders]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const sortAndFilterItems = (items) => {
    let filtered = items;
    
    // Apply search filter
    if (search.trim()) {
      const query = search.toLowerCase();
      filtered = items.filter(item => 
        item.item_name.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  };

  const startEdit = (item) => {
    setEditingItem(item.item_name);
    setEditValue(item.buffer_level.toString());
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditValue('');
  };

  const saveEdit = async (item) => {
    const newBuffer = parseInt(editValue);
    if (isNaN(newBuffer) || newBuffer < 0) {
      toast.error('Please enter a valid buffer value');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/buffer-review/${encodeURIComponent(item.item_name)}?buffer_level=${newBuffer}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Buffer updated for ${item.item_name}`);
      setEditingItem(null);
      setEditValue('');
      // Refresh data
      fetchData();
    } catch (error) {
      console.error('Error updating buffer:', error);
      toast.error('Failed to update buffer level');
    } finally {
      setSaving(false);
    }
  };

  const applySuggested = async (item) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/buffer-review/${encodeURIComponent(item.item_name)}?buffer_level=${item.suggested_buffer}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Buffer updated to suggested value for ${item.item_name}`);
      fetchData();
    } catch (error) {
      console.error('Error updating buffer:', error);
      toast.error('Failed to update buffer level');
    } finally {
      setSaving(false);
    }
  };

  const exportToExcel = (items, type) => {
    if (items.length === 0) {
      toast.error('No data to export');
      return;
    }

    const exportData = items.map(item => ({
      'SKU': item.sku,
      'Item Name': item.item_name,
      'Current Buffer': item.buffer_level,
      'Avg Sale': item.avg_sale,
      'Ratio (Buffer/AvgSale)': item.ratio,
      'Suggested Buffer': item.suggested_buffer,
      'Total Stock': item.total_stock,
      'Issue': item.issue === 'too_high' ? 'Buffer Too High' : 'Buffer Too Low'
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    ws['!cols'] = [
      { wch: 18 }, { wch: 35 }, { wch: 14 }, { wch: 12 }, 
      { wch: 18 }, { wch: 16 }, { wch: 12 }, { wch: 16 }
    ];
    
    XLSX.utils.book_append_sheet(wb, ws, 'Buffer Review');
    XLSX.writeFile(wb, `buffer_review_${type}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(`Exported ${items.length} items to Excel`);
  };

  const renderTable = (items, type) => {
    const filteredItems = sortAndFilterItems(items);
    
    return (
      <div className="space-y-4">
        {/* Search and Export */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by Item Name or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="buffer-search"
            />
          </div>
          <Button variant="outline" onClick={() => exportToExcel(filteredItems, type)}>
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Count badge */}
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{filteredItems.length} items</Badge>
          {search && <span className="text-sm text-slate-500">filtered from {items.length}</span>}
        </div>

        {/* Table */}
        {filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Check className="w-16 h-16 text-green-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">
              {search ? 'No items match your search' : 'No buffer anomalies found'}
            </p>
            <p className="text-slate-400 text-sm">
              {!search && 'All buffer levels are within acceptable range'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="data-table">
              <thead>
                <tr>
                  <th 
                    className="cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('sku')}
                  >
                    <span className="flex items-center">SKU {getSortIcon('sku')}</span>
                  </th>
                  <th 
                    className="cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('item_name')}
                  >
                    <span className="flex items-center">Item Name {getSortIcon('item_name')}</span>
                  </th>
                  <th 
                    className="text-right cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('buffer_level')}
                  >
                    <span className="flex items-center justify-end">Current Buffer {getSortIcon('buffer_level')}</span>
                  </th>
                  <th 
                    className="text-right cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('avg_sale')}
                  >
                    <span className="flex items-center justify-end">Avg Sale {getSortIcon('avg_sale')}</span>
                  </th>
                  <th 
                    className="text-right cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('ratio')}
                  >
                    <span className="flex items-center justify-end">Ratio {getSortIcon('ratio')}</span>
                  </th>
                  <th className="text-right">Suggested</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.item_name} className={type === 'too_high' ? 'bg-orange-50' : 'bg-red-50'}>
                    <td className="font-mono text-sm">{item.sku}</td>
                    <td className="font-medium">{item.item_name}</td>
                    <td className="numeric">
                      {editingItem === item.item_name ? (
                        <Input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-24 h-8 text-right"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit(item);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                      ) : (
                        <span className={type === 'too_high' ? 'text-orange-600 font-bold' : 'text-red-600 font-bold'}>
                          {item.buffer_level.toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className="numeric text-blue-600">{item.avg_sale.toLocaleString()}</td>
                    <td className="numeric">
                      <Badge 
                        variant="secondary" 
                        className={type === 'too_high' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}
                      >
                        {item.ratio}x
                      </Badge>
                    </td>
                    <td className="numeric">
                      <div className="flex items-center justify-end gap-1">
                        <Lightbulb className="w-3 h-3 text-yellow-500" />
                        <span className="text-green-600 font-medium">{item.suggested_buffer.toLocaleString()}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center justify-center gap-1">
                        {editingItem === item.item_name ? (
                          <>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => saveEdit(item)}
                              disabled={saving}
                              className="h-8 w-8 p-0"
                            >
                              <Save className="w-4 h-4 text-green-600" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={cancelEdit}
                              className="h-8 w-8 p-0"
                            >
                              <X className="w-4 h-4 text-slate-500" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => startEdit(item)}
                              className="h-8 w-8 p-0"
                              title="Edit buffer"
                            >
                              <Edit2 className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => applySuggested(item)}
                              disabled={saving}
                              className="h-8 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Apply suggested buffer"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Apply
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Show loading overlay
  if (loading) {
    return <LoadingOverlay message="Analyzing buffer levels..." />;
  }

  // Show error state
  if (error) {
    return <ErrorState 
      title="Failed to load buffer review" 
      message={error} 
      onRetry={fetchData}
    />;
  }

  return (
    <div className="space-y-6" data-testid="buffer-review-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Buffer Review
        </h1>
        <p className="text-slate-500 mt-1">Review and adjust buffer stock levels that may be too high or too low</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Items Analyzed</p>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">
                  {data.summary.total_items || 0}
                </p>
              </div>
              <div className="bg-blue-100 p-2 rounded-lg">
                <Search className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Buffer Too High</p>
                <p className="text-2xl font-bold text-orange-600 tabular-nums">
                  {data.summary.too_high_count || 0}
                </p>
                <p className="text-xs text-slate-400">Buffer &gt; 5× Avg Sale</p>
              </div>
              <div className="bg-orange-100 p-2 rounded-lg">
                <TrendingUp className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Buffer Too Low</p>
                <p className="text-2xl font-bold text-red-600 tabular-nums">
                  {data.summary.too_low_count || 0}
                </p>
                <p className="text-xs text-slate-400">Buffer &lt; Avg Sale</p>
              </div>
              <div className="bg-red-100 p-2 rounded-lg">
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card>
        <CardContent className="p-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Tabs defaultValue="too_high" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="too_high" className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Buffer Too High
                  <Badge variant="secondary" className="ml-1 bg-orange-100 text-orange-700">
                    {data.too_high.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="too_low" className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4" />
                  Buffer Too Low
                  <Badge variant="secondary" className="ml-1 bg-red-100 text-red-700">
                    {data.too_low.length}
                  </Badge>
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="too_high">
                <div className="mb-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-orange-800">Buffer levels are unusually high</p>
                      <p className="text-sm text-orange-600">
                        These items have buffer stock more than 5× their average monthly sale. 
                        Consider reducing buffer to free up capital.
                      </p>
                    </div>
                  </div>
                </div>
                {renderTable(data.too_high, 'too_high')}
              </TabsContent>
              
              <TabsContent value="too_low">
                <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800">Buffer levels may be insufficient</p>
                      <p className="text-sm text-red-600">
                        These items have buffer stock less than their average monthly sale. 
                        Consider increasing buffer to prevent stockouts.
                      </p>
                    </div>
                  </div>
                </div>
                {renderTable(data.too_low, 'too_low')}
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BufferReview;
